/**
 * Nesting Level Assignment
 *
 * Assigns nesting levels to back-edges and feedback nodes.
 *
 * NESTING LEVEL RULES:
 * - Critical Path nodes have level 0
 * - Level 1: back-edge target is a Critical Path node
 * - Level N: back-edge target is a Level N-1 feedback node
 *
 * Returns:
 *   nodeLevels         - Map<nodeId, number>
 *   backEdgesWithLevels - Array of { from, to, level, targetLevel, isNested? }
 */
export function assignNestingLevels(tasks, backEdgeList, backEdges, feedbackNodes, parentsMap, childrenMap) {
  const nodeLevels = new Map();

  // Critical path nodes start at level 0
  for (const task of tasks) {
    if (!feedbackNodes.has(task.id)) nodeLevels.set(task.id, 0);
  }

  // Initial level guess for each back-edge based on its target
  const backEdgesWithLevels = backEdgeList.map(edge => {
    const targetLevel = nodeLevels.get(edge.to);
    const resolved = targetLevel !== undefined;
    return {
      ...edge,
      level: resolved ? targetLevel + 1 : -1,
      targetLevel: resolved ? targetLevel : -1,
    };
  });

  const maxIterations = backEdgeList.length * 3 + 2;
  let iterations = 0;

  while (iterations < maxIterations) {
    iterations++;
    let madeProgress = false;

    // Pass 1: resolve back-edges whose target now has a known level
    for (const backEdge of backEdgesWithLevels) {
      if (backEdge.level !== -1) continue;
      const targetLevel = nodeLevels.get(backEdge.to);
      if (targetLevel !== undefined) {
        backEdge.level = targetLevel + 1;
        backEdge.targetLevel = targetLevel;
        madeProgress = true;
      }
    }

    // Pass 2: resolve back-edges whose target is still an unresolved feedback node
    // by finding the entry level from the critical path into the loop
    for (const backEdge of backEdgesWithLevels) {
      if (backEdge.level !== -1) continue;

      // Collect all feedback nodes in this loop
      const loopNodeIds = new Set();
      const queue = [backEdge.from, backEdge.to];
      const visited = new Set();

      while (queue.length > 0) {
        const nodeId = queue.shift();
        if (visited.has(nodeId)) continue;
        visited.add(nodeId);

        if (feedbackNodes.has(nodeId)) {
          loopNodeIds.add(nodeId);

          for (const parentId of (parentsMap.get(nodeId) || [])) {
            const edgeKey = `${parentId}->${nodeId}`;
            if (!backEdges.has(edgeKey) && !visited.has(parentId)) queue.push(parentId);
          }
          for (const childId of (childrenMap.get(nodeId) || [])) {
            const edgeKey = `${nodeId}->${childId}`;
            if (!backEdges.has(edgeKey) && !visited.has(childId)) queue.push(childId);
          }
        }
      }

      // Entry level = highest level of any non-loop parent of a loop node
      let entryLevel = -1;
      for (const nodeId of loopNodeIds) {
        for (const parentId of (parentsMap.get(nodeId) || [])) {
          const parentLevel = nodeLevels.get(parentId);
          if (parentLevel !== undefined && (!feedbackNodes.has(parentId) || !loopNodeIds.has(parentId))) {
            entryLevel = Math.max(entryLevel, parentLevel);
          }
        }
      }

      if (entryLevel >= 0) {
        backEdge.level = entryLevel + 1;
        backEdge.targetLevel = entryLevel;
        madeProgress = true;

        for (const nodeId of loopNodeIds) {
          if (!nodeLevels.has(nodeId)) nodeLevels.set(nodeId, backEdge.level);
        }
      }
    }

    // Pass 3: assign levels to feedback nodes belonging to resolved back-edges
    for (const backEdge of backEdgesWithLevels) {
      if (backEdge.level === -1) continue;

      const queue = [backEdge.from];
      const visited = new Set();

      while (queue.length > 0) {
        const nodeId = queue.shift();
        if (visited.has(nodeId)) continue;
        visited.add(nodeId);

        if (feedbackNodes.has(nodeId) && !nodeLevels.has(nodeId)) {
          nodeLevels.set(nodeId, backEdge.level);
          madeProgress = true;
        }

        for (const parentId of (parentsMap.get(nodeId) || [])) {
          const edgeKey = `${parentId}->${nodeId}`;
          if (!backEdges.has(edgeKey) && feedbackNodes.has(parentId) && !visited.has(parentId)) {
            queue.push(parentId);
          }
        }
      }
    }

    if (!madeProgress) break;
  }

  // Default any still-unassigned feedback nodes to level 1
  for (const nodeId of feedbackNodes) {
    if (!nodeLevels.has(nodeId)) nodeLevels.set(nodeId, 1);
  }

  // Default any still-unresolved back-edges to level 1
  for (const backEdge of backEdgesWithLevels) {
    if (backEdge.level === -1) {
      backEdge.level = 1;
      backEdge.targetLevel = 0;
    }
  }

  return { nodeLevels, backEdgesWithLevels };
}
